import { Component, OnDestroy, OnInit } from '@angular/core';
import { Header } from '@approot/models/ag-grid.model';
import { AgGridService, ConfigService } from '@approot/services';
import {
  DateUtilities,
  reformatDate,
  SubscriptionSink
} from '@approot/utilities';
import { SideBarDef, GridApi, ColumnApi } from 'ag-grid-community';
import { DeltaHedgesService } from './delta-hedges.service';
import { DatePipe } from '@angular/common';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { take, tap } from 'rxjs';
import DropDownOptions from '@approot/modals/trade-capture/schedule-reports/report-dropdown-options.json';

export interface DeltaHedgeDto {
  hedgingStrategy: string;
  orderId: string;
  sourceDealReference: string;
  sourceRequestId: string;
  status: string;
  timestamp_str: string;
}

@Component({
  selector: 'app-delta-hedges',
  templateUrl: './delta-hedges.component.html',
  styleUrls: ['./delta-hedges.component.scss']
})
export class DeltaHedgesComponent implements OnInit, OnDestroy {
  public columnDefs: Header[];
  public sideBarDef: SideBarDef;
  public gridApi: GridApi;
  private subSink = new SubscriptionSink();
  public deltaHedgesForm: FormGroup;
  public timezones: string[] = DropDownOptions.timezones;
  public hoursArray: string[] = Array.from({ length: 24 }, (_, i) =>
    this.pad(i)
  );
  public minutesArray = ['00', '15', '30', '45'];
  public columnApi: ColumnApi;

  constructor(
    private agGridService: AgGridService,
    private configService: ConfigService,
    private deltaHedgesService: DeltaHedgesService,
    private datePipe: DatePipe
  ) {}

  public ngOnInit(): void {
    this.deltaHedgesForm = new FormGroup({
      fromDate: new FormControl(reformatDate(new Date(), 'D MMM YYYY'), [
        Validators.required
      ]),
      fromHour: new FormControl('01', [Validators.required]),
      fromMinute: new FormControl('00', [Validators.required]),
      timezone: new FormControl<string>('Europe/London', [Validators.required])
    });
    this.sideBarDef = this.agGridService.sideBarDef;
    this.subSink.sink = this.configService.getUserSettings().subscribe(x => {
      const headers = x.ag_grid_config.find(
        y => y.identifier === 'deltaHedgesOrdersHeaders'
      )?.headers as string[];

      if (headers) {
        this.columnDefs = this.agGridService.formatHeaders(
          headers as unknown as Header[]
        );
      }
    });
    this.subSink.sink = this.deltaHedgesForm.valueChanges
      .pipe(
        tap(_ => {
          if (this.deltaHedgesForm.valid) {
            this.getDeltaHedgingOrders();
          }
        })
      )
      .subscribe();
    this.getDeltaHedgingOrders();
  }

  public ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  public onGridReady(params): void {
    this.gridApi = params.api;
    this.gridApi.setRowData([]);
    this.gridApi.sizeColumnsToFit();
    this.columnApi = params.columnApi;
  }

  public onDateChange(): void {
    const formDateCtrl = this.deltaHedgesForm.controls['fromDate'];
    setTimeout(() => {
      if (DateUtilities.isDateOfSlashFormat(formDateCtrl.value)) {
        formDateCtrl.setValue(reformatDate(formDateCtrl.value, 'D MMM YYYY'));
      }
    }, 0);
  }

  private convertDate(dateStr: string): string {
    if (DateUtilities.isDateOfSlashFormat(dateStr)) {
      const date = reformatDate(dateStr, 'D MMM YYYY');
      return this.datePipe.transform(date, 'yyyy-MM-dd') || '';
    } else {
      return this.datePipe.transform(new Date(dateStr), 'yyyy-MM-dd') || '';
    }
  }

  private getDeltaHedgingOrders(): void {
    const date = this.convertDate(this.deltaHedgesForm.get('fromDate').value);
    const hour = this.deltaHedgesForm.get('fromHour').value;
    const minute = this.deltaHedgesForm.get('fromMinute').value;
    const timezone = this.deltaHedgesForm.get('timezone').value;
    const formattedTime = `${hour}:${minute}:00`;
    const time = formattedTime;

    this.gridApi?.showLoadingOverlay();
    if (date && time && timezone) {
      this.deltaHedgesService
        .getDeltaHedgingOrders(date, time, timezone)
        .pipe(
          tap((statusResponse: { orders: DeltaHedgeDto[] }) => {
            this.gridApi.setRowData(statusResponse.orders);
          }),
          take(1)
        )
        .subscribe();
    }
  }

  private pad(num: number): string {
    return num < 10 ? '0' + num : num.toString();
  }
}
